import { useCallback, useMemo, useState } from 'react';
import useUrlFilters, { UrlFilters } from '../../../hooks/useUrlFilters';
import { distinctArrayBy, getValue } from '../../../utils/common';
import { Column } from '../types';

type Props<T extends object> = {
  data: Array<T>;
  columns: Column<T>[];
  searchValue?: string;
  urlParamsPrefix?: string;
  searchFunc?: (value: string, rowData: T) => void;
  isControlled?: boolean;
  isFilterOptionsLoading?: boolean;
};

type FilterDataProps<T extends object> = {
  data: Array<T>;
  columns: Column<T>[];
  urlFilters?: UrlFilters;
  searchValue?: string;
  searchFunc?: (value: string, rowData: T) => void;
  isControlled?: boolean;
};

const filterData = <T extends object>({
  data,
  columns,
  searchValue,
  searchFunc,
  urlFilters,
  isControlled,
}: FilterDataProps<T>) => {
  if ((!urlFilters && !searchValue) || isControlled) return data;

  let result = data;

  if (searchValue) {
    result = data.filter((item) => {
      if (searchFunc) {
        return searchFunc(searchValue, item);
      }
      return Object.values(item).some((val) =>
        JSON.stringify(val)?.toLowerCase().includes(searchValue.toLowerCase()),
      );
    });
  }

  if (urlFilters) {
    result = result.filter((item) =>
      urlFilters?.every((filter) => {
        const column = columns.find((c) => c.id === filter.property);

        if (column?.filterValueMatcher) {
          return column.filterValueMatcher(
            filter.value || [],
            item,
            filter.operator,
          );
        }

        const lowerCaseFilterValue = filter.value.map((val) =>
          val?.toString().toLowerCase(),
        );

        const tableCellValue = getValue(item, filter.property);

        const isValueArray = Array.isArray(tableCellValue);
        const lowerCaseCellValue = isValueArray
          ? tableCellValue.map((cellVal) => cellVal.toString().toLowerCase())
          : tableCellValue?.toString().toLowerCase() || '';

        if (isValueArray) {
          const includeEmpty = filter.value.some((i) => !i);

          switch (filter?.operator) {
            case 'equals':
              return (
                lowerCaseCellValue?.some((cv: string) =>
                  lowerCaseFilterValue.includes(cv),
                ) || (includeEmpty ? lowerCaseCellValue.length === 0 : false)
              );

            case 'contains':
              return lowerCaseCellValue?.some((cv: string) =>
                lowerCaseFilterValue.some((fv) => fv.includes(cv)),
              );

            case 'notEquals':
              return !(
                lowerCaseCellValue?.some((cv: string) =>
                  lowerCaseFilterValue.includes(cv),
                ) || (includeEmpty ? lowerCaseCellValue.length === 0 : false)
              );

            case 'notContains':
              return !lowerCaseCellValue?.some((cv: string) =>
                lowerCaseFilterValue.some((fv) => fv.includes(cv)),
              );

            default:
              return false;
          }
        }

        switch (filter.operator) {
          case 'equals':
            return lowerCaseFilterValue
              ? lowerCaseFilterValue.includes(lowerCaseCellValue)
              : !lowerCaseCellValue;
          case 'contains':
            return lowerCaseFilterValue.some((filterValue) =>
              lowerCaseCellValue.includes(filterValue),
            );
          case 'notEquals':
            return lowerCaseFilterValue
              ? !lowerCaseFilterValue.includes(lowerCaseCellValue)
              : lowerCaseCellValue;
          case 'notContains':
            return lowerCaseFilterValue.every(
              (filterValue) => !lowerCaseCellValue.includes(filterValue),
            );
          default:
            return false;
        }
      }),
    );
  }

  return result;
};

const useTableFilteredData = <T extends object>({
  data,
  columns,
  searchValue = '',
  urlParamsPrefix,
  searchFunc,
  isControlled,
  isFilterOptionsLoading,
}: Props<T>) => {
  const { urlFilters, clearAllFilters } = useUrlFilters({
    urlKey: urlParamsPrefix,
  });

  const filteredData = useMemo(
    () =>
      filterData({
        data,
        columns,
        searchValue,
        searchFunc,
        urlFilters,
        isControlled,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchFunc, searchValue, urlFilters, isControlled, data],
  );

  const filteredDataByTopFilters = useMemo(
    () =>
      filterData({
        data,
        columns,
        searchValue,
        searchFunc,
        urlFilters: urlFilters?.filter((f) => f.operator !== 'equals'),
        isControlled,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchFunc, searchValue, isControlled, urlFilters, data],
  );

  // Memoization cache for getFilterOptions
  const [filterOptionsCache, setFilterOptionsCache] = useState<
    Map<string, any>
  >(() => new Map<string, any>());

  const MAX_CACHE_SIZE = 100; // Adjust based on your needs

  // Create a cache key based on parameters and dependencies
  const createCacheKey = useCallback(
    (columnId: string, isCounterHidden: boolean) => {
      const columnsKey = columns
        .map(
          (c) =>
            `${c.id}-${
              c.type
            }-${!!c.filterValueMatcher}-${!!c.filterOptionsOverride}`,
        )
        .join('|');
      const urlFiltersKey = JSON.stringify(
        urlFilters
          ?.map((f) => `${f.property}-${f.operator}-${f.value?.join(',')}`)
          .sort(),
      );
      const filteredDataKey = filteredData.length.toString();
      const filteredDataByTopFiltersKey =
        filteredDataByTopFilters.length.toString();

      return `${columnId}-${isCounterHidden}-${columnsKey}-${urlFiltersKey}-${filteredDataKey}-${filteredDataByTopFiltersKey}`;
    },
    [columns, urlFilters, filteredData, filteredDataByTopFilters],
  );

  const getFilterOptions = useCallback(
    (columnId: string, isCounterHidden: boolean) => {
      if (isFilterOptionsLoading) return [];

      const cacheKey = createCacheKey(columnId, isCounterHidden);

      // Check if we have a cached result
      if (filterOptionsCache.has(cacheKey)) {
        return filterOptionsCache.get(cacheKey);
      }

      const col = columns.find((c) => c.id === columnId);

      const filterIsActive = urlFilters?.some((f) => f.property === columnId);
      const activeFilters = (
        urlFilters?.filter(
          (f) => f.property === columnId && f.operator === 'equals',
        ) || []
      ).flatMap((f) => f.value || []);

      const dataToUse = filterIsActive
        ? filteredDataByTopFilters
        : filteredData;

      const getCounter = (value: string) => {
        if (isCounterHidden || col?.type === 'date') return 0;

        if (col?.filterValueMatcher) {
          return (
            dataToUse.filter((row) =>
              col?.filterValueMatcher?.([value], row, 'equals'),
            ).length || 0
          );
        }

        return (
          dataToUse.filter((row) => {
            const cellValue = getValue(row, columnId);

            if (Array.isArray(cellValue)) {
              if (!value) return cellValue.length === 0;

              return cellValue.some(
                (j) => j?.toString().toLowerCase() === value?.toLowerCase(),
              );
            }

            return (
              (cellValue?.toString().toLowerCase() || '') ===
              value?.toLowerCase()
            );
          }).length || 0
        );
      };

      let result;

      if (col?.filterOptionsOverride) {
        if (isCounterHidden) {
          result = col.filterOptionsOverride;
        } else {
          result = col.filterOptionsOverride
            .map((i) => ({
              ...i,
              counter: i.count || getCounter(i.value),
            }))
            .filter((i) => i.counter);
        }
      } else {
        const columnData = dataToUse
          .map((item) => {
            const label = getValue(item, columnId)?.toString() || '';

            return { value: label.toLowerCase(), label };
          })
          .concat(activeFilters.map((f) => ({ value: f, label: f })));

        const columnValues = distinctArrayBy(columnData, 'value');

        if (isCounterHidden) {
          result = columnValues;
        } else {
          result = columnValues.map((i) => ({
            ...i,
            counter: getCounter(i.value),
          }));
        }
      }

      // Cache the result
      setFilterOptionsCache((prevCache) => {
        const newCache = new Map(prevCache);

        if (newCache.size >= MAX_CACHE_SIZE) {
          const firstKey = newCache.keys().next().value;
          if (firstKey) {
            newCache.delete(firstKey);
          }
        }

        newCache.set(cacheKey, result);
        return newCache;
      });

      return result;
    },
    [
      columns,
      filteredDataByTopFilters,
      filteredData,
      urlFilters,
      createCacheKey,
      filterOptionsCache,
      isFilterOptionsLoading,
    ],
  );

  return {
    getFilterOptions,
    filteredData,
    clearAllFilters,
  };
};

export default useTableFilteredData;
